import { useEffect, useId, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import type { LoggedInUser } from '../../../../types/auth.types';
import { EXPRESS_CONSENT_TEXT, EXPRESS_VALIDITY_TEXT, type ExpressStatus } from '../../../../common/expressConsent';
import newAPIService from '../../../api/newAPIService';
import Button from '../../foundation/Button';
import Alert from '../../foundation/Alert';
import {
    ExpressOfferModal, OfferActions, OfferContent, OfferDescription, OfferHeader, OfferHero, OfferTitle, OfferValidity,
    SuccessHero,
} from './ExpressDialogs.styles';

export const expressCvKey = (userid: number) => `express:registration-cv:${userid}`;

export function ExpressOfferDialog({
    open, registration = false, expired = false, busy = false, error,
    onApprove, onRegular, onClose,
}: {
    open: boolean; registration?: boolean; expired?: boolean; busy?: boolean; error?: string;
    onApprove: () => void; onRegular?: () => void; onClose: () => void;
}) {
    const titleId = useId();
    let approvalText = registration ? 'אישור אמפלוי אקספרס והרשמה' : 'אישור אמפלוי אקספרס';
    if (busy) approvalText = 'שומרים את האישור';
    return <ExpressOfferModal open={open} onClose={busy ? undefined : onClose} aria-labelledby={titleId} fullWidth maxWidth="md">
        <OfferContent>
            <OfferHeader>
                <bdi dir="ltr">Mploy Express</bdi>
                <IconButton aria-label="סגירת הסבר אמפלוי אקספרס" onClick={onClose} disabled={busy}><Close /></IconButton>
            </OfferHeader>
            <OfferHero>
                <div>
                    <OfferTitle id={titleId}>{expired ? 'חידוש אמפלוי אקספרס' : 'תנו לקורות החיים לעבוד בשבילכם'}</OfferTitle>
                    <OfferDescription>
                        {expired ? 'האישור שלך פג לאחר תקופה ללא פעילות, אפשר לחדש אותו ולהמשיך להגיע למעסיקים מתאימים'
                            : 'עם אמפלוי אקספרס נמליץ עליך ונשלח את קורות החיים שלך למעסיקים מתאימים — כדי לפתוח לך עוד הזדמנויות'}
                    </OfferDescription>
                </div>
                <img src="/images/resume-upload/express-home.png" alt="" width="1780" height="876" />
            </OfferHero>
            <Stack spacing={2.5}>
                <OfferValidity>{EXPRESS_VALIDITY_TEXT}</OfferValidity>
                <Typography variant="body2" color="text.secondary">{EXPRESS_CONSENT_TEXT}</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <OfferActions>
                    <Button disabled={busy} onClick={onApprove} fullWidth>{approvalText}</Button>
                    {registration && <Button disabled={busy} onClick={onRegular} variant="outlined" fullWidth>הרשמה רגילה</Button>}
                </OfferActions>
            </Stack>
        </OfferContent>
    </ExpressOfferModal>;
}

export function ExpressCvDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const titleId = useId();
    const descriptionId = useId();
    return <ExpressOfferModal open={open} onClose={onClose} aria-labelledby={titleId}
        aria-describedby={descriptionId} fullWidth maxWidth="md">
        <OfferContent>
            <OfferHeader>
                <bdi dir="ltr">Mploy Express</bdi>
                <IconButton aria-label="סגירת אישור אמפלוי אקספרס" onClick={onClose}><Close /></IconButton>
            </OfferHeader>
            <SuccessHero>
                <div>
                    <OfferTitle id={titleId}>אמפלוי אקספרס אושר!</OfferTitle>
                    <OfferDescription id={descriptionId}>
                        עדיין אין לך קורות חיים באתר, כדי שנוכל לשלוח אותם למעסיקים נשאר רק להעלות קובץ
                    </OfferDescription>
                </div>
                <img src="/images/resume-upload/express-success.png" alt="" width="1254" height="1254" />
            </SuccessHero>
            <OfferActions>
                <Button href="/resume-landing-page" fullWidth>העלאת קורות חיים</Button>
            </OfferActions>
        </OfferContent>
    </ExpressOfferModal>;
}

export default function ExpressDialogs({ user }: { user?: LoggedInUser }) {
    const [offer, setOffer] = useState<ExpressStatus | null>(null);
    const [showCv, setShowCv] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setOffer(null);
        setShowCv(false);
        setError('');
        try {
            const previousUser = sessionStorage.getItem('express:last-user');
            if (previousUser && previousUser !== String(user?.userid)) {
                sessionStorage.removeItem(`express:checked:${previousUser}`);
            }
            if (user) sessionStorage.setItem('express:last-user', String(user.userid));
            else sessionStorage.removeItem('express:last-user');
        } catch { /* Storage is optional. */ }
        if (!user || user.isEmployer || window.location.pathname === '/resume-landing-page') return undefined;
        const checkedKey = `express:checked:${user.userid}`;
        const cvKey = expressCvKey(user.userid);
        let cancelled = false;
        const check = async () => {
            try {
                const registration = sessionStorage.getItem(cvKey) === '1';
                if (!registration && sessionStorage.getItem(checkedKey)) return;
                if (registration) {
                    const { data } = await newAPIService.get<ExpressStatus>('/api/express/status');
                    if (cancelled) return;
                    sessionStorage.removeItem(cvKey);
                    setShowCv(data.approved && !data.hasCV);
                } else {
                    const { data } = await newAPIService.post<ExpressStatus>('/api/express/prompt', {});
                    if (cancelled) return;
                    if (data.promptPending && !data.approved) setOffer(data);
                }
                sessionStorage.setItem(checkedKey, '1');
            } catch { /* A prompt must not block navigating the site; retry on the next page load. */ }
        };
        // Defer until mounted, including React's development setup/cleanup cycle.
        const timer = window.setTimeout(check, 0);
        return () => { cancelled = true; window.clearTimeout(timer); };
    }, [user?.userid, user?.isEmployer]);

    const approve = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            const { data } = await newAPIService.post<ExpressStatus>('/api/express/approve', {});
            setOffer(null);
            setShowCv(!data.hasCV);
        } catch { setError('לא הצלחנו לשמור את האישור, אפשר לנסות שוב'); } finally { setBusy(false); }
    };

    return <>
        <ExpressOfferDialog open={!!offer} expired={offer?.disabledReason === 'system'} busy={busy}
            error={error} onClose={() => setOffer(null)} onApprove={approve} />
        <ExpressCvDialog open={showCv} onClose={() => setShowCv(false)} />
    </>;
}
